import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {addMonths, endOfYear, startOfYear} from "date-fns";
import {
    getAsseCurrent,
    getAssetByType,
    getAssetHistory,
    getContract,
    getDepreciation,
    getSimmsLicense,
} from "../../api/static/StaticService";
import {toKSTISOString, toKSTISOStringEnd} from "../../components/commonmethod/CommonMethod";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {
    AssetSummaryCard,
    extractResponseData,
    formatPeriod,
    HistoryCalendarCard,
    LicenseSummaryCard,
    TypeChartCard,
} from "./AssetDashboardShared";

function StaticAssetForITM() {
    const today = useMemo(() => new Date(), []);

    const threeMonthSearch = useMemo(() => ({
        startDate: toKSTISOString(addMonths(today, -3)),
        endDate: toKSTISOStringEnd(today),
    }), [today]);

    const thisYearSearch = useMemo(() => ({
        startDate: toKSTISOString(startOfYear(today)),
        endDate: toKSTISOStringEnd(endOfYear(today)),
    }), [today]);

    const thisMonthAndClick = useMemo(() => {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
            startDate: toKSTISOString(firstDay),
            endDate: toKSTISOStringEnd(lastDay),
            clickedDate: toKSTISOString(today),
        };
    }, [today]);

    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        current: null,
        depreciation: null,
        contract: null,
        license: null,
        byType: null,
        history: null,
    });

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        const settled = await Promise.allSettled([
            getAsseCurrent(threeMonthSearch),
            getDepreciation(),
            getContract(thisYearSearch),
            getSimmsLicense(),
            getAssetByType(),
            getAssetHistory(thisMonthAndClick),
        ]);

        setDashboardData({
            current: extractResponseData(settled[0]),
            depreciation: extractResponseData(settled[1]),
            contract: extractResponseData(settled[2]),
            license: extractResponseData(settled[3]),
            byType: extractResponseData(settled[4]),
            history: extractResponseData(settled[5]),
        });
        setLoading(false);
    }, [threeMonthSearch, thisYearSearch, thisMonthAndClick]);

    const reloadHistory = useCallback(async (search) => {
        try {
            const response = await getAssetHistory(search);
            const data = response?.status === 200 ? response.data : null;
            setDashboardData((prev) => ({...prev, history: data}));
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const assetCurrentItems = dashboardData.current?.data ?? [];
    const depreciationItems = dashboardData.depreciation?.data ?? [];
    const contractItems = dashboardData.contract?.data ?? [];

    const licenseItems = dashboardData.license?.data ?? [];
    const licenseTotal = Number(licenseItems[0]?.cnt) || 0;
    const licenseUsed = Number(licenseItems[1]?.cnt) || 0;
    const licensePlatform = dashboardData.license?.title;

    const groupItems = dashboardData.byType?.data?.group ?? [];
    const taskItems = dashboardData.byType?.data?.task ?? [];
    const locationItems = dashboardData.byType?.data?.location ?? [];

    return (
        <div className="common-container static-asset-dashboard relative flex min-h-0 flex-col">
            {loading && <ChartLoadingOverlay />}

            <div
                className="flex flex-col gap-4 md:grid md:min-h-0 md:flex-1 md:[grid-template-columns:repeat(4,minmax(0,1fr))] md:[grid-template-rows:repeat(3,minmax(0,1fr))]"
            >
                <div className="flex min-h-[220px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-1 md:block md:min-h-0">
                    <AssetSummaryCard
                        title={dashboardData.current?.title ?? "자산종합관리"}
                        items={assetCurrentItems}
                        period={formatPeriod(threeMonthSearch)}
                    />
                </div>
                <div className="flex min-h-[220px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-1 md:block md:min-h-0">
                    <AssetSummaryCard
                        title={dashboardData.depreciation?.title ?? "감가상각/EOS"}
                        items={depreciationItems}
                    />
                </div>
                <div className="flex min-h-[220px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-1 md:block md:min-h-0">
                    <AssetSummaryCard
                        title={dashboardData.contract?.title ?? "자산계약관리"}
                        items={contractItems}
                        period={formatPeriod(thisYearSearch)}
                    />
                </div>
                <div className="flex min-h-[220px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-1 md:block md:min-h-0">
                    <LicenseSummaryCard
                        title="라이선스 현황"
                        total={licenseTotal}
                        used={licenseUsed}
                        platform={licensePlatform}
                    />
                </div>

                <div className="flex min-h-[340px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-2 md:block md:min-h-0">
                    <TypeChartCard
                        title="자산유형별 현황"
                        subtitle="자산그룹"
                        items={groupItems}
                    />
                </div>
                <div className="flex min-h-[340px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-2 md:block md:min-h-0">
                    <TypeChartCard
                        title="자산유형별 현황"
                        subtitle="업무그룹"
                        items={taskItems}
                    />
                </div>

                <div className="flex min-h-[340px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-2 md:block md:min-h-0">
                    <TypeChartCard
                        title="자산유형별 현황"
                        subtitle="자산위치"
                        items={locationItems}
                    />
                </div>
                <div className="flex min-h-[380px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:col-span-2 md:block md:min-h-0">
                    <HistoryCalendarCard
                        history={dashboardData.history}
                        onReload={reloadHistory}
                    />
                </div>
            </div>
        </div>
    );
}

export default StaticAssetForITM;
